/**
 * Estado local de notificaciones (leídas / borradas) por usuario.
 *
 * Las alertas se computan en vivo desde el estado del sistema, así que
 * no hay forma de "borrarlas" en origen — lo que persistimos acá es la
 * decisión del usuario sobre cuáles ya vio y cuáles dismissed.
 *
 * Storage: localStorage clave `pmis.notif.<emailLower>`. Mantenemos un
 * registro por usuario para que distintos roles vean su propia tachuela.
 *
 * Patrón `useSyncExternalStore` con cache estable (igual que
 * reportesAuth.ts) — evita el bug "snapshot should be cached".
 */

import { useSyncExternalStore } from "react";

interface EstadoUsuario {
  /** IDs de alertas que el usuario marcó como leídas. */
  leidas: string[];
  /** IDs de alertas que el usuario borró (no quiere ver más). */
  borradas: string[];
}

const EVENT_NAME = "pmis:notifEstado:changed";
const listeners = new Set<() => void>();

// Cache: snapshot estable por email.
const _cache = new Map<string, EstadoUsuario>();

if (typeof window !== "undefined") {
  window.addEventListener(EVENT_NAME, () => {
    // Invalidación total del cache. Es barato — el set es pequeño.
    _cache.clear();
  });
}

function storageKey(email: string): string {
  return `pmis.notif.${email.toLowerCase()}`;
}

function leerDelStorage(email: string): EstadoUsuario {
  try {
    if (typeof window === "undefined") return { leidas: [], borradas: [] };
    const raw = localStorage.getItem(storageKey(email));
    if (!raw) return { leidas: [], borradas: [] };
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      Array.isArray((parsed as EstadoUsuario).leidas) &&
      Array.isArray((parsed as EstadoUsuario).borradas)
    ) {
      return parsed as EstadoUsuario;
    }
    return { leidas: [], borradas: [] };
  } catch {
    return { leidas: [], borradas: [] };
  }
}

function leerEstado(email: string): EstadoUsuario {
  if (!email) return { leidas: [], borradas: [] };
  const cached = _cache.get(email);
  if (cached) return cached;
  const fresco = leerDelStorage(email);
  _cache.set(email, fresco);
  return fresco;
}

function guardar(email: string, estado: EstadoUsuario): void {
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem(storageKey(email), JSON.stringify(estado));
    }
  } catch {
    // ignore — modo privado / quota
  }
  _cache.delete(email);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  }
  for (const l of listeners) l();
}

// ════════════════════════════════════════════════════════════════════
// API pública
// ════════════════════════════════════════════════════════════════════

export function marcarLeida(email: string, alertaId: string): void {
  const e = leerEstado(email);
  if (e.leidas.includes(alertaId)) return;
  guardar(email, { ...e, leidas: [...e.leidas, alertaId] });
}

export function marcarTodasLeidas(email: string, alertaIds: string[]): void {
  const e = leerEstado(email);
  const set = new Set([...e.leidas, ...alertaIds]);
  guardar(email, { ...e, leidas: Array.from(set) });
}

export function borrarAlerta(email: string, alertaId: string): void {
  const e = leerEstado(email);
  if (e.borradas.includes(alertaId)) return;
  guardar(email, { ...e, borradas: [...e.borradas, alertaId] });
}

export function restaurarBorradas(email: string): void {
  const e = leerEstado(email);
  guardar(email, { ...e, borradas: [] });
}

/**
 * Hook reactivo — devuelve el estado del usuario y se re-renderea
 * cuando cambia (ej. al marcar/borrar desde otra parte de la UI).
 */
export function useNotificacionesEstado(email: string): EstadoUsuario {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      const handler = () => cb();
      if (typeof window !== "undefined") {
        window.addEventListener(EVENT_NAME, handler);
      }
      return () => {
        listeners.delete(cb);
        if (typeof window !== "undefined") {
          window.removeEventListener(EVENT_NAME, handler);
        }
      };
    },
    () => leerEstado(email),
    () => leerEstado(email),
  );
}
